import { FIT_MESSAGE, RESIZE_MESSAGE } from "./constants.js";
import type { Bridge, FitPayload } from "./types.js";

/** Options for {@link startAutoResize}. */
export interface AutoResizeOptions {
  /**
   * Minimum change (in px) on either axis before a new size is reported. Guards
   * against sub-pixel churn and `ResizeObserver` feedback loops. Default `1`.
   */
  threshold?: number;
}

/**
 * Observe the document's content box and emit {@link RESIZE_MESSAGE} to the host
 * whenever it changes, so the host can size a `fit`/auto container to the content.
 *
 * The content's *intrinsic* size is measured from the document body's
 * `scrollWidth/scrollHeight` — NOT `documentElement`, whose scroll size is floored
 * at the iframe viewport height. Measuring the root would create an
 * observe→resize→observe feedback loop: once the host sizes the iframe to a
 * placeholder taller than the content, `documentElement.scrollHeight` returns the
 * viewport height, the SDK reports that back, and the frame stays pinned at the
 * placeholder instead of shrinking to the content. The body's scroll box reflects
 * the intrinsic content and is independent of the iframe size. Reports are
 * coalesced to an animation frame and only sent when the value moves past
 * `threshold`.
 *
 * Safe to call in any environment: if there is no DOM or `ResizeObserver`, it is a
 * no-op and returns a no-op teardown.
 *
 * @returns a teardown that disconnects the observer and removes listeners.
 */
export function startAutoResize(
  bridge: Pick<Bridge, "send" | "on">,
  win: Window & typeof globalThis,
  options: AutoResizeOptions = {},
): () => void {
  const threshold = options.threshold ?? 1;
  const doc = win.document as Document | undefined;
  if (!doc || typeof win.ResizeObserver !== "function") {
    return () => {};
  }

  // When the host reports it is sizing an axis to our content (`fit`), suppress
  // the scrollbar on that axis. The host makes the frame exactly as large as the
  // content, so the only time a scrollbar would show is the brief window while the
  // host catches up to a just-reported size — a flicker we don't want. Applied to
  // the scrolling root (`documentElement`); measurement is unaffected because
  // `scrollHeight` still counts overflowing content. Restored on teardown.
  const applyFit = (payload: unknown): void => {
    const root = doc.documentElement;
    if (!root) return;
    const { width, height } = (payload ?? {}) as FitPayload;
    root.style.overflowX = width ? "hidden" : "";
    root.style.overflowY = height ? "hidden" : "";
  };
  const offFit = bridge.on(FIT_MESSAGE, applyFit);

  let lastWidth = -1;
  let lastHeight = -1;
  let scheduled = false;
  let stopped = false;
  let frameHandle = 0;

  // The element whose scroll box reflects the intrinsic content size. The body
  // (not `documentElement`) is used because the root's scroll size is floored at
  // the iframe viewport; see the function docstring. Falls back to the root only
  // if there is no body (e.g. an unusual document).
  const contentEl = (): Element | null => doc.body ?? doc.documentElement;

  const measureAndSend = (): void => {
    if (stopped) return;
    scheduled = false;
    const root = contentEl();
    if (!root) return;
    const width = Math.ceil(root.scrollWidth);
    const height = Math.ceil(root.scrollHeight);
    if (
      Math.abs(width - lastWidth) < threshold &&
      Math.abs(height - lastHeight) < threshold
    ) {
      return;
    }
    lastWidth = width;
    lastHeight = height;
    bridge.send(RESIZE_MESSAGE, { width, height });
  };

  const schedule = (): void => {
    if (scheduled) return;
    scheduled = true;
    frameHandle =
      typeof win.requestAnimationFrame === "function"
        ? win.requestAnimationFrame(measureAndSend)
        : (setTimeout(measureAndSend, 0) as unknown as number);
  };

  const observer = new win.ResizeObserver(schedule);

  const begin = (): void => {
    const root = contentEl();
    if (!root) return;
    observer.observe(root);
    // Late-loading images/fonts reflow the document after the observer attaches.
    win.addEventListener("load", schedule);
    measureAndSend();
  };

  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", begin, { once: true });
  } else {
    begin();
  }

  return (): void => {
    stopped = true;
    if (frameHandle) {
      // frameHandle is a requestAnimationFrame OR a setTimeout id depending on
      // which was available; cancel it with the matching canceller so a pending
      // callback can't fire after teardown.
      if (typeof win.cancelAnimationFrame === "function") {
        win.cancelAnimationFrame(frameHandle);
      } else {
        clearTimeout(frameHandle);
      }
    }
    observer.disconnect();
    win.removeEventListener("load", schedule);
    doc.removeEventListener("DOMContentLoaded", begin);
    offFit();
    // Restore any scrollbar we suppressed so teardown leaves the document as found.
    const root = doc.documentElement;
    if (root) {
      root.style.overflowX = "";
      root.style.overflowY = "";
    }
  };
}
